import { TriangleDownIcon } from '@primer/octicons-react';
import { ActionList, Box } from '@primer/react';
import { SelectPanel } from '@primer/react/experimental';
import { ImpactLensType } from '@upright/common/impact-lenses/types';
import { usePushGtmEvent } from '@upright/ui/common/generic/components/google-tag-manager/use-push-gtm-event';
import { CustomGtmEvent } from '@upright/ui/common/generic/components/google-tag-manager/use-push-gtm-event/payloads';
import { useCarouselContext } from '../../../impact-lens-profile/context';
import {
  visualRelatedViewOptionsKeys,
  NetImpactLensSelectionState,
  NetImpactLensViewOptionKey,
  useGetAvailableNumberRelatedViewOptionsKeys,
} from '../selection-state';
import { useState } from 'react';

export function NetImpactLensViewOptionsSelector() {
  const context = useCarouselContext<ImpactLensType.UPRIGHT_NET_IMPACT>();
  const [isOpen, setIsOpen] = useState(false);

  const { selectionState, setSelectionState } = context;

  const pushGtmEvent = usePushGtmEvent();

  const onOpenSelection = () => {
    pushGtmEvent({
      event: CustomGtmEvent.IMPACT_PROFILE_NI_VIEW_OPTIONS_CLICK,
    });
    setIsOpen(true);
  };

  const handleSelection = (selectionOption: NetImpactLensViewOptionKey) => {
    setSelectionState({ ...selectionState, [selectionOption]: !selectionState[selectionOption] });
  };

  const dimensionalityAndFiguresViewOptionKeys = useGetAvailableNumberRelatedViewOptionsKeys();

  return (
    <Box>
      <SelectPanel
        title={'View options'}
        selectionVariant={'multiple'}
        width="small"
        height="fit-content"
        open={isOpen}
        onCancel={() => {
          setIsOpen(false);
        }}
      >
        <SelectPanel.Button
          size="small"
          variant="invisible"
          trailingVisual={TriangleDownIcon}
          onClick={onOpenSelection}
        >
          View options
        </SelectPanel.Button>
        <ActionList>
          <Box>
            <ActionList.GroupHeading>Dimensions and figures</ActionList.GroupHeading>
            {dimensionalityAndFiguresViewOptionKeys.map(key => (
              <ActionList.Item
                key={key}
                onSelect={() => {
                  handleSelection(key);
                }}
                selected={isSelected(selectionState, key)}
              >
                {getTitle(key)}
              </ActionList.Item>
            ))}
          </Box>
          <Box>
            <ActionList.GroupHeading>Insights and visuals</ActionList.GroupHeading>
            {visualRelatedViewOptionsKeys.map(key => (
              <ActionList.Item
                key={key}
                onSelect={() => {
                  handleSelection(key);
                }}
                selected={isSelected(selectionState, key)}
              >
                {getTitle(key)}
              </ActionList.Item>
            ))}
          </Box>
        </ActionList>
      </SelectPanel>
    </Box>
  );
}

const isSelected = (state: NetImpactLensSelectionState, key: NetImpactLensViewOptionKey) => {
  switch (key) {
    case 'showAllNetFigures':
    case 'showTotalSums':
    case 'showCategoryPercentiles':
    case 'showDimensionBars':
    case 'showValueChains':
      return state[key];
    case 'hideKeyTakeawayBoxes':
    case 'hideCurrencyUnits':
      return !state[key];
  }
};

const getTitle = (selectionOption: NetImpactLensViewOptionKey) => {
  switch (selectionOption) {
    case 'hideKeyTakeawayBoxes':
      return 'Show takeaway boxes';
    case 'hideCurrencyUnits':
      return 'Show currency units';
    case 'showAllNetFigures':
      return 'Show all net figures';
    case 'showDimensionBars':
      return 'Show dimension bars';
    case 'showValueChains':
      return 'Show value chains';
    case 'showTotalSums':
      return 'Show total sums';
    case 'showCategoryPercentiles':
      return 'Show category percentiles';
  }
};
